import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Button, FoodsIcon, FoodsTags, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { getConsentVersionStatus, isConsentVersionArchived } from "../consentVersionStatus";
import { BulkActionBar, ConsentActionIconButton, EllipsisTooltip } from "./ConsentAdminShared";
import {
    formatConsentVersionActivePeriod,
    getConsentItemName,
    getConsentVersionTitle,
    getConsentVersionStatusDescriptor,
} from "../utils/consentGovernanceFormatters";

type ConsentVersionsTabProps = {
    allVisibleSelected: boolean;
    bulkActionLoading: boolean;
    consentItems: ConsentGovernanceItem[];
    filteredVersions: ConsentGovernanceVersion[];
    isLoading: boolean;
    page: number;
    paginatedVersions: ConsentGovernanceVersion[];
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    selectedVersionIds: Set<number>;
    selectedVersions: ConsentGovernanceVersion[];
    totalVersions: number;
    uiLocale: string;
    versionPurposeFilter: number | null;
    onAddVersion: () => void;
    onBulkDeleteVersions: (versions: ConsentGovernanceVersion[]) => void;
    onDeleteVersion: (version: ConsentGovernanceVersion) => void;
    onEditVersion: (version: ConsentGovernanceVersion) => void;
    onPageChange: (page: number) => void;
    onPreviewVersion: (version: ConsentGovernanceVersion) => void;
    onPurposeFilterChange: (itemId: number | null) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onSetVisibleVersionSelection: (versions: ConsentGovernanceVersion[], selected: boolean) => void;
    onToggleVersionSelection: (versionId: number, selected: boolean) => void;
};

export const ConsentVersionsTab = ({
    allVisibleSelected,
    bulkActionLoading,
    consentItems,
    filteredVersions,
    isLoading,
    page,
    paginatedVersions,
    rowsPerPage,
    rowsPerPageOptions,
    selectedVersionIds,
    selectedVersions,
    totalVersions,
    uiLocale,
    versionPurposeFilter,
    onAddVersion,
    onBulkDeleteVersions,
    onDeleteVersion,
    onEditVersion,
    onPageChange,
    onPreviewVersion,
    onPurposeFilterChange,
    onRowsPerPageChange,
    onSetVisibleVersionSelection,
    onToggleVersionSelection,
}: ConsentVersionsTabProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const tableTextSx = { ...theme.typography.body2, color: theme.custom.grey[2800] };

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                gap={theme.spacing(2)}
            >
                <Typography variant="body" sx={{ fontWeight: 700 }}>
                    {t("settings.consent.versions.title", "Version history")}
                </Typography>
                <Button
                    variant="validate"
                    size="small"
                    startIcon={<FoodsIcon icon="add" size={1} />}
                    onClick={onAddVersion}
                    disabled={consentItems.length === 0}
                    sx={{
                        justifyContent: "center",
                        minHeight: 44,
                        minWidth: 190,
                        width: { xs: "100%", sm: "auto" },
                        textTransform: "none",
                    }}
                >
                    {t("settings.consent.versions.actions.add_version", "Add version")}
                </Button>
            </Stack>

            {consentItems.length > 0 && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip
                        label={t("settings.consent.versions.all_purposes", "All")}
                        size="small"
                        onClick={() => onPurposeFilterChange(null)}
                        sx={{
                            fontWeight: 600,
                            bgcolor:
                                versionPurposeFilter === null ? theme.palette.primary.main : theme.palette.grey[100],
                            color: versionPurposeFilter === null ? "white" : theme.custom.grey[2000],
                            "&:hover": {
                                bgcolor:
                                    versionPurposeFilter === null ?
                                        theme.palette.primary.dark
                                    :   theme.palette.grey[200],
                            },
                        }}
                    />
                    {consentItems.map((item) => (
                        <Chip
                            key={item.id}
                            label={item.name}
                            size="small"
                            onClick={() => onPurposeFilterChange(item.id)}
                            sx={{
                                fontWeight: 600,
                                bgcolor:
                                    versionPurposeFilter === item.id ?
                                        theme.palette.primary.main
                                    :   theme.palette.grey[100],
                                color: versionPurposeFilter === item.id ? "white" : theme.custom.grey[2000],
                                "&:hover": {
                                    bgcolor:
                                        versionPurposeFilter === item.id ?
                                            theme.palette.primary.dark
                                        :   theme.palette.grey[200],
                                },
                            }}
                        />
                    ))}
                </Stack>
            )}

            {isLoading ?
                <Stack minHeight="200px" alignItems="center" justifyContent="center">
                    <CircularProgress />
                </Stack>
            : filteredVersions.length === 0 ?
                <WhiteBox padding={theme.spacing(4)} boxShadow={theme.custom.shadows.grey}>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000], textAlign: "center" }}>
                        {t("settings.consent.versions.empty.title", "No consent version found")}
                    </Typography>
                </WhiteBox>
            :   <Stack gap={theme.spacing(1.5)}>
                    {selectedVersions.length > 0 && (
                        <Box>
                            <BulkActionBar
                                count={selectedVersions.length}
                                onClear={() => onSetVisibleVersionSelection(paginatedVersions, false)}
                            >
                                <Button
                                    size="small"
                                    variant="delete"
                                    disabled={bulkActionLoading}
                                    startIcon={<FoodsIcon icon="delete" size={1} />}
                                    onClick={() => onBulkDeleteVersions(selectedVersions)}
                                    sx={{
                                        justifyContent: "center",
                                        minHeight: 44,
                                        minWidth: 150,
                                        textTransform: "none",
                                    }}
                                >
                                    {t("common.actions.delete", "Delete")}
                                </Button>
                            </BulkActionBar>
                        </Box>
                    )}
                    <WhiteBox
                        padding={0}
                        boxShadow={theme.custom.shadows.grey}
                        sx={{ maxWidth: "100%", overflow: "hidden" }}
                    >
                        <Box sx={{ maxWidth: "100%", overflowX: { xs: "auto", lg: "hidden" } }}>
                            <Table
                                size="small"
                                sx={{ width: "100%", minWidth: { xs: 1120, lg: 0 }, tableLayout: "fixed" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" sx={{ width: 56 }}>
                                            <Checkbox
                                                checked={allVisibleSelected}
                                                indeterminate={selectedVersions.length > 0 && !allVisibleSelected}
                                                inputProps={{
                                                    "aria-label": t(
                                                        "settings.consent.selection.select_visible_versions",
                                                        "Select visible versions",
                                                    ),
                                                }}
                                                onChange={(event) =>
                                                    onSetVisibleVersionSelection(
                                                        paginatedVersions,
                                                        event.target.checked,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: 210, whiteSpace: "nowrap" }}>
                                            {t("settings.consent.version.version", "Version")}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {t("settings.consent.version.title_col", "Version title")}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {t("settings.consent.version.item_col", "Consent item")}
                                        </TableCell>
                                        <TableCell sx={{ width: 280, whiteSpace: "nowrap", pr: theme.spacing(3) }}>
                                            {t("settings.consent.version.active_period", "Active period")}
                                        </TableCell>
                                        <TableCell sx={{ width: 150, whiteSpace: "nowrap" }}>
                                            {t("settings.consent.common.status", "Status")}
                                        </TableCell>
                                        <TableCell sx={{ width: 188, whiteSpace: "nowrap" }}>
                                            {t("settings.consent.version.actions_col", "Actions")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedVersions.map((version) => {
                                        const versionStatus = getConsentVersionStatus(version);
                                        const isCurrent = versionStatus === "published";
                                        const isArchived = isConsentVersionArchived(version);
                                        const statusDescriptor = getConsentVersionStatusDescriptor(version);
                                        const activePeriod = formatConsentVersionActivePeriod(
                                            version,
                                            uiLocale,
                                            t("settings.consent.versions.current_period", "present"),
                                        );
                                        const versionTitle = getConsentVersionTitle(version);
                                        const itemName = getConsentItemName(version.consentItem);

                                        return (
                                            <TableRow key={version.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedVersionIds.has(version.id)}
                                                        inputProps={{
                                                            "aria-label": t(
                                                                "settings.consent.selection.select_version",
                                                                "Select version {{version}}",
                                                                { version: version.version },
                                                            ),
                                                        }}
                                                        onChange={(event) =>
                                                            onToggleVersionSelection(version.id, event.target.checked)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                        flexWrap="nowrap"
                                                    >
                                                        <FoodsTags label={`v${version.version}`} variant="tags-grey" />
                                                        {isCurrent && (
                                                            <FoodsTags
                                                                label={t(
                                                                    "settings.consent.versions.current",
                                                                    "Current",
                                                                )}
                                                                variant="tags-green"
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <EllipsisTooltip
                                                        title={versionTitle}
                                                        sx={{
                                                            ...tableTextSx,
                                                            color:
                                                                versionTitle ?
                                                                    theme.custom.grey[2800]
                                                                :   theme.custom.grey[2000],
                                                            fontWeight: versionTitle ? 600 : 400,
                                                        }}
                                                    >
                                                        {versionTitle ?? "—"}
                                                    </EllipsisTooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <EllipsisTooltip
                                                        title={itemName}
                                                        sx={{ ...tableTextSx, fontWeight: 700 }}
                                                    >
                                                        {itemName}
                                                    </EllipsisTooltip>
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 0, pr: theme.spacing(3) }}>
                                                    <EllipsisTooltip title={activePeriod} sx={tableTextSx}>
                                                        {activePeriod}
                                                    </EllipsisTooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <FoodsTags
                                                        label={t(
                                                            statusDescriptor.labelKey,
                                                            statusDescriptor.labelFallback,
                                                        )}
                                                        variant={statusDescriptor.tagVariant}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 0 }}>
                                                    <Stack
                                                        direction="row"
                                                        gap={0.75}
                                                        flexWrap="nowrap"
                                                        sx={{
                                                            width: "100%",
                                                            minWidth: 0,
                                                            justifyContent: "flex-start",
                                                        }}
                                                    >
                                                        <ConsentActionIconButton
                                                            icon="edit"
                                                            label={t("common.actions.edit", "Edit")}
                                                            disabled={isArchived}
                                                            disabledTooltip={t(
                                                                "settings.consent.version.archived_read_only",
                                                                "Archived versions are read-only to preserve the audit trail.",
                                                            )}
                                                            onClick={() => onEditVersion(version)}
                                                            color={theme.palette.primary.main}
                                                        />
                                                        <ConsentActionIconButton
                                                            icon="viewPwd"
                                                            label={t("settings.consent.preview.action", "Preview")}
                                                            onClick={() => onPreviewVersion(version)}
                                                            color={theme.palette.info.main}
                                                        />
                                                        <ConsentActionIconButton
                                                            icon="delete"
                                                            label={t("common.actions.delete", "Delete")}
                                                            onClick={() => onDeleteVersion(version)}
                                                            color={theme.palette.error.main}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            component="div"
                            count={totalVersions}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onPageChange={(_, nextPage) => {
                                onPageChange(nextPage);
                                onSetVisibleVersionSelection(paginatedVersions, false);
                            }}
                            onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                        />
                    </WhiteBox>
                </Stack>
            }
        </Stack>
    );
};
